import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  HEALTHCHECK_NAMES,
  ZONE_KEYS,
  useNewProjectUrl,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { Button } from 'framework/ui/mui-builder';
import { getHealthcheckByName } from '../../../shared/healthcheck';
import { RequiredActionItem } from '../../commercial/sections/RequiredActionsSection';
import { PpcLinkConfirmAction } from '../PpcLinkActions';
import { PPC_LINK_NOT_CONFIRMED_MESSAGE } from '../../../engineering/constants';
import { ControlData } from '../../types';

type EngineeringRequiredActionsProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
};

export const useEngineeringRequiredActions = ({
  data,
  refetchControlsData,
}: EngineeringRequiredActionsProps): RequiredActionItem[] => {
  const navigate = useNavigate();
  const { getEngineeringUrl } = useNewProjectUrl();
  const { isAccessGranted } = useProjectSecurityContext();

  return useMemo(() => {
    const healthChecks = data?.health?.healthChecks || [];
    const ppcLinkHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.ENGINEERING_LINK);
    const ppcLink = data?.engineeringPolicy?.ppcLink;
    const canManageEngineering = isAccessGranted(ACCESS_INFO_SECTIONS.ENGINEERING, ACCESS_INFO_ACTIONS.MANAGE);

    const actions: RequiredActionItem[] = [];

    if (!canManageEngineering || !ppcLinkHealthcheck) {
      return actions;
    }

    if (!ppcLink) {
      actions.push({
        id: 'ppc-link-not-defined',
        message: 'PPC link is not defined.',
        actions: (
          <Button variant="contained" onClick={() => navigate(getEngineeringUrl(data.id))}>
            Define
          </Button>
        ),
      });
    } else if (ppcLinkHealthcheck.targetZone !== ZONE_KEYS.SAFE) {
      actions.push({
        id: 'ppc-link-not-confirmed',
        message: PPC_LINK_NOT_CONFIRMED_MESSAGE,
        actions: <PpcLinkConfirmAction data={data} refetchControlsData={refetchControlsData} />,
      });
    }

    return actions;
  }, [data, getEngineeringUrl, isAccessGranted, navigate, refetchControlsData]);
};
